import type { KeywordConceptType, KeywordRule, MatchRule } from "../types";
import { KEYWORD_BOUNDARY_CLASS } from "./keyword-boundaries";

export interface ArticleRuleAnalysis {
  ruleId: string;
  keyword: string;
  conceptType: KeywordConceptType | "keyword";
  contextual: boolean;
  familyKey: string;
  specificity: number;
  contentBlockCount: number;
  contextBlockCount: number;
  rawMatches: number;
  leadMatch: boolean;
  score: number;
  selected: boolean;
  blockedReason?: "absent" | "context_insufficient" | "generic_singleton" | "shadowed" | "over_limit";
}

export interface SelectArticleRulesInput {
  rules: MatchRule[];
  contentBlocks: string[];
  contextBlocks?: string[];
  maxKeywordRules?: number;
}

export interface SelectArticleRulesResult {
  selectedRules: MatchRule[];
  analyses: ArticleRuleAnalysis[];
  matchedKeywordCount: number;
  matchedRawCount: number;
  selectedKeywordCount: number;
  selectedRawCount: number;
}

const DEFAULT_ARTICLE_RULE_LIMIT = 15;
const FAMILY_SPECIFIC_CONCEPTS = new Set<KeywordConceptType>([
  "brand_family_phrase",
  "brand_model_phrase",
  "model_line",
  "manual_alias"
]);
const HIGH_VALUE_CONCEPTS = new Set<KeywordConceptType>([
  "brand",
  "brand_family_phrase",
  "brand_model_phrase",
  "platform_term",
  "accessory_phrase",
  "acronym_category",
  "model_line",
  "manual_alias"
]);

export function selectArticleRules(input: SelectArticleRulesInput): SelectArticleRulesResult {
  const contentBlocks = normalizeBlocks(input.contentBlocks);
  if (contentBlocks.length === 0) {
    return {
      selectedRules: input.rules,
      analyses: [],
      matchedKeywordCount: 0,
      matchedRawCount: 0,
      selectedKeywordCount: 0,
      selectedRawCount: 0
    };
  }

  const contextBlocks = normalizeBlocks(input.contextBlocks ?? []);
  const keywordLimit = Math.max(1, input.maxKeywordRules ?? DEFAULT_ARTICLE_RULE_LIMIT);
  const analysesByRuleId = new Map<string, ArticleRuleAnalysis>();
  const keywordCandidates: ArticleRuleAnalysis[] = [];
  const regexSelections = new Set<string>();

  for (const rule of input.rules) {
    if (rule.type === "regex") {
      if (countRuleMatches(rule, contentBlocks).rawMatches > 0) {
        regexSelections.add(rule.id);
      }
      continue;
    }

    const metrics = countRuleMatches(rule, contentBlocks);
    const contextMetrics = countRuleMatches(rule, contextBlocks);
    const keyword = getCanonicalKeyword(rule);
    const conceptType = rule.conceptType ?? inferConceptType(rule);
    const analysis: ArticleRuleAnalysis = {
      ruleId: rule.id,
      keyword,
      conceptType,
      contextual: rule.contextual ?? conceptType === "contextual_product_head",
      familyKey: rule.familyKey ?? deriveFamilyKey(keyword, conceptType),
      specificity: rule.specificity ?? inferSpecificity(rule, conceptType),
      contentBlockCount: metrics.contentBlockCount,
      contextBlockCount: contextMetrics.contentBlockCount,
      rawMatches: metrics.rawMatches,
      leadMatch: metrics.leadMatch,
      score: 0,
      selected: false
    };

    if (analysis.rawMatches === 0) {
      analysis.blockedReason = "absent";
      analysesByRuleId.set(rule.id, analysis);
      continue;
    }

    keywordCandidates.push(analysis);
    analysesByRuleId.set(rule.id, analysis);
  }

  const specificAnchorCount = keywordCandidates.filter(isSpecificAnchorCandidate).length;

  const eligibleCandidates = keywordCandidates
    .map((analysis) => {
      if (analysis.contextual && !passesContextualThreshold(analysis, specificAnchorCount)) {
        return {
          ...analysis,
          blockedReason: "context_insufficient" as const
        };
      }

      if (isWeakGenericSingleton(analysis, specificAnchorCount)) {
        return {
          ...analysis,
          blockedReason: "generic_singleton" as const
        };
      }

      return {
        ...analysis,
        score: computeArticleRuleScore(analysis)
      };
    })
    .sort(compareArticleAnalyses);

  const selectedById = new Set<string>();
  const selectedAnalyses: ArticleRuleAnalysis[] = [];

  for (const candidate of eligibleCandidates) {
    if (candidate.blockedReason) {
      analysesByRuleId.set(candidate.ruleId, candidate);
      continue;
    }

    if (selectedAnalyses.length >= keywordLimit) {
      candidate.blockedReason = "over_limit";
      analysesByRuleId.set(candidate.ruleId, candidate);
      continue;
    }

    if (isShadowedByExistingSelection(candidate, selectedAnalyses)) {
      candidate.blockedReason = "shadowed";
      analysesByRuleId.set(candidate.ruleId, candidate);
      continue;
    }

    candidate.selected = true;
    selectedById.add(candidate.ruleId);
    selectedAnalyses.push(candidate);
    analysesByRuleId.set(candidate.ruleId, candidate);
  }

  const selectedRules = input.rules.filter((rule) => {
    if (rule.type === "regex") {
      return regexSelections.has(rule.id);
    }
    return selectedById.has(rule.id);
  });

  const analyses = input.rules
    .filter((rule): rule is KeywordRule => rule.type === "keyword")
    .map((rule) => analysesByRuleId.get(rule.id))
    .filter((analysis): analysis is ArticleRuleAnalysis => Boolean(analysis));

  return {
    selectedRules,
    analyses: analyses.sort(compareArticleAnalyses),
    matchedKeywordCount: keywordCandidates.length,
    matchedRawCount: keywordCandidates.reduce((sum, analysis) => sum + analysis.rawMatches, 0),
    selectedKeywordCount: selectedAnalyses.length,
    selectedRawCount: selectedAnalyses.reduce((sum, analysis) => sum + analysis.rawMatches, 0)
  };
}

function normalizeBlocks(blocks: string[]): string[] {
  return blocks
    .map((block) => block.replace(/\s+/g, " ").trim())
    .filter((block) => block.length > 0);
}

function countRuleMatches(rule: MatchRule, blocks: string[]): { rawMatches: number; contentBlockCount: number; leadMatch: boolean } {
  if (blocks.length === 0) {
    return { rawMatches: 0, contentBlockCount: 0, leadMatch: false };
  }

  if (rule.type === "regex") {
    const flags = rule.flags?.includes("g") ? rule.flags : `${rule.flags ?? ""}g`;
    let rawMatches = 0;
    let contentBlockCount = 0;
    let leadMatch = false;

    for (let index = 0; index < blocks.length; index += 1) {
      const regex = new RegExp(rule.pattern, flags);
      const local = blocks[index].match(regex);
      if (!local || local.length === 0) {
        continue;
      }

      rawMatches += local.length;
      contentBlockCount += 1;
      leadMatch ||= index === 0;
    }

    return { rawMatches, contentBlockCount, leadMatch };
  }

  const patterns = Array.isArray(rule.pattern) ? rule.pattern : [rule.pattern];
  let rawMatches = 0;
  let contentBlockCount = 0;
  let leadMatch = false;

  for (let index = 0; index < blocks.length; index += 1) {
    const block = blocks[index];
    let matchedThisBlock = false;

    for (const pattern of patterns) {
      const regex = buildKeywordRegex(pattern, rule.wholeWord ?? true);
      const local = block.match(regex);
      if (!local || local.length === 0) {
        continue;
      }

      rawMatches += local.length;
      matchedThisBlock = true;
    }

    if (!matchedThisBlock) {
      continue;
    }

    contentBlockCount += 1;
    leadMatch ||= index === 0;
  }

  return { rawMatches, contentBlockCount, leadMatch };
}

function buildKeywordRegex(pattern: string, wholeWord: boolean): RegExp {
  const escaped = escapeRegex(pattern.trim()).replace(/\s+/g, "[\\s-]+");
  if (!wholeWord) {
    return new RegExp(escaped, "giu");
  }

  return new RegExp(`(^|${KEYWORD_BOUNDARY_CLASS})(${escaped})(?=$|${KEYWORD_BOUNDARY_CLASS})`, "giu");
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function getCanonicalKeyword(rule: KeywordRule): string {
  if (rule.canonicalKeyword?.trim()) {
    return normalizeKeyword(rule.canonicalKeyword);
  }

  if (Array.isArray(rule.pattern)) {
    return normalizeKeyword(rule.pattern[0] ?? "");
  }

  return normalizeKeyword(rule.pattern);
}

function normalizeKeyword(value: string): string {
  return value
    .toLocaleLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function inferConceptType(rule: KeywordRule): KeywordConceptType | "keyword" {
  if (rule.contextual) {
    return "contextual_product_head";
  }

  const tokens = getCanonicalKeyword(rule).split(/\s+/).filter((token) => token.length > 0);
  if (tokens.length >= 3) {
    return "brand_model_phrase";
  }
  if (tokens.length === 2) {
    return "brand_family_phrase";
  }
  return "keyword";
}

function inferSpecificity(rule: KeywordRule, conceptType: KeywordConceptType | "keyword"): number {
  const tokenCount = getCanonicalKeyword(rule).split(/\s+/).filter((token) => token.length > 0).length;
  const base =
    conceptType === "brand_model_phrase" || conceptType === "model_line"
      ? 4
      : conceptType === "brand_family_phrase" || conceptType === "accessory_phrase" || conceptType === "platform_term"
        ? 3
        : conceptType === "brand" || conceptType === "acronym_category"
          ? 2
          : conceptType === "contextual_product_head"
            ? 1
            : 2;

  return Math.max(base, Math.min(tokenCount + 1, 4));
}

function deriveFamilyKey(keyword: string, conceptType: KeywordConceptType | "keyword"): string {
  const tokens = keyword.split(/\s+/).filter((token) => token.length > 0);
  if (tokens.length <= 1) {
    return keyword;
  }

  if (FAMILY_SPECIFIC_CONCEPTS.has(conceptType as KeywordConceptType)) {
    const trailing = tokens[tokens.length - 1];
    if (isModelLikeToken(trailing)) {
      return tokens.slice(0, -1).join(" ");
    }
    return keyword;
  }

  if (conceptType === "contextual_product_head") {
    return tokens[0];
  }

  return keyword;
}

function isModelLikeToken(token: string): boolean {
  return /^\d{1,4}$/.test(token) || /^[a-z]\d{1,3}$/i.test(token) || /^[mx]\d$/i.test(token);
}

function isSpecificAnchorCandidate(analysis: ArticleRuleAnalysis): boolean {
  return (
    !analysis.contextual &&
    analysis.rawMatches > 0 &&
    analysis.specificity >= 3 &&
    analysis.conceptType !== "keyword" &&
    HIGH_VALUE_CONCEPTS.has(analysis.conceptType)
  );
}

function passesContextualThreshold(analysis: ArticleRuleAnalysis, specificAnchorCount: number): boolean {
  return analysis.contentBlockCount >= 3 || (analysis.leadMatch && specificAnchorCount >= 2);
}

function isWeakGenericSingleton(analysis: ArticleRuleAnalysis, specificAnchorCount: number): boolean {
  const tokenCount = analysis.keyword.split(/\s+/).filter((token) => token.length > 0).length;
  if (tokenCount !== 1 || specificAnchorCount < 2) {
    return false;
  }

  if (analysis.contextual || analysis.conceptType !== "keyword" && HIGH_VALUE_CONCEPTS.has(analysis.conceptType)) {
    return false;
  }

  return analysis.specificity <= 1;
}

function computeArticleRuleScore(analysis: ArticleRuleAnalysis): number {
  const baseScores: Record<ArticleRuleAnalysis["conceptType"], number> = {
    brand: 92,
    brand_family_phrase: 110,
    brand_model_phrase: 116,
    platform_term: 96,
    accessory_phrase: 102,
    contextual_product_head: 74,
    acronym_category: 86,
    category_phrase: 72,
    model_line: 106,
    manual_alias: 118,
    keyword: 70
  };

  let score = baseScores[analysis.conceptType] ?? 70;
  score += analysis.specificity * 10;
  score += analysis.contentBlockCount * 7;
  score += analysis.contextBlockCount * 9;
  score += Math.min(analysis.rawMatches, 8) * 3;
  if (analysis.leadMatch) {
    score += 12;
  }
  if (analysis.contextual) {
    score -= 8;
  }

  return score;
}

function compareArticleAnalyses(left: ArticleRuleAnalysis, right: ArticleRuleAnalysis): number {
  if (right.selected !== left.selected) {
    return Number(right.selected) - Number(left.selected);
  }
  if (right.score !== left.score) {
    return right.score - left.score;
  }
  if (right.specificity !== left.specificity) {
    return right.specificity - left.specificity;
  }
  if (right.contentBlockCount !== left.contentBlockCount) {
    return right.contentBlockCount - left.contentBlockCount;
  }
  return left.keyword.localeCompare(right.keyword);
}

function isShadowedByExistingSelection(candidate: ArticleRuleAnalysis, selected: ArticleRuleAnalysis[]): boolean {
  for (const existing of selected) {
    if (existing.specificity <= candidate.specificity) {
      continue;
    }

    if (candidate.familyKey && candidate.familyKey === existing.familyKey) {
      return true;
    }

    if (tokenSequenceContained(candidate.keyword, existing.keyword)) {
      return true;
    }
  }

  return false;
}

function tokenSequenceContained(candidateKeyword: string, selectedKeyword: string): boolean {
  const candidateTokens = candidateKeyword.split(/\s+/).filter((token) => token.length > 0);
  const selectedTokens = selectedKeyword.split(/\s+/).filter((token) => token.length > 0);
  if (candidateTokens.length === 0 || candidateTokens.length >= selectedTokens.length) {
    return false;
  }

  for (let index = 0; index <= selectedTokens.length - candidateTokens.length; index += 1) {
    let matches = true;
    for (let offset = 0; offset < candidateTokens.length; offset += 1) {
      if (selectedTokens[index + offset] !== candidateTokens[offset]) {
        matches = false;
        break;
      }
    }

    if (matches) {
      return true;
    }
  }

  return false;
}
